import '../account/Drones.css';
import './Console.css';
import { useState, useEffect, useCallback, useMemo, useRef, ReactNode } from 'react'
import { logger } from '../../utils/logger'
import { Button, Offcanvas, Modal } from "react-bootstrap";
import ConsoleNavMenu from './ConsoleNavMenu';
import DroneList from './DroneList';
import DroneGrid from './DroneGrid';
import ConnectionStatus from './ConnectionStatus';
import ConsoleMap from './ConsoleMap';
import NavigationData from './NavigationData';
import ModemSettings from './ModemSettings'
import WebRtc from './webRtc/WebRtc';
import Fpv from './fpv/Fpv';
import FpvModeButton from './fpv/FpvModeButton';
import useDroneHubConnection from '../../hooks/DroneHubConnection';
import useDroneFilter from '../../hooks/useDroneFilter';
import useFleetState from '../../hooks/useFleetState';
import useDroneTriage from '../../hooks/useDroneTriage';
import { HubHbStatusMessage, HubSetOnlineMessage } from '../../asp/DroneHub';
import { ModemData, MultipleModemsSchema, StartMessageData } from '../../api/Drone';
import { useAccess } from '../../access/PlatformAccess';
import { APIRequestGet } from '../../utils/APIRequests';

type ImeiMap = { [key: string]: HTMLDivElement };

type ConsoleView = 'map' | 'grid';

const VIEW_STORAGE_KEY = 'console.view';

function Console(): ReactNode {
    const droneHub = useDroneHubConnection();
    const access = useAccess();
    const [updateOnlineDroneFromHub, setUpdateDroneFromHub] = useState<HubSetOnlineMessage | null>(null);
    const [updateHbFromHub, setUpdateHbFromHub] = useState<HubHbStatusMessage | null>(null);

    const [mode, setMode] = useState<string>("normal");
    const [drones, setDrones] = useState<ModemData[]>([]);
    const [drone, setDrone] = useState<ModemData | null>(null);
    const [mapFocusOnDrone, setMapFocusOnDrone] = useState<boolean>(false);
    const [_isConfirmEmail, setIsConfirmEmail] = useState<boolean>(false);
    const [isNebosvodAuth, setIsNebosvodAuth] = useState<boolean>(false);
    const [nebosvodAuthLogin, setNebosvodLogin] = useState<string>("");
    const [startMsgs, setStartMsgs] = useState<StartMessageData[]>([]);

    const [isLoad, setIsLoad] = useState<boolean>(false);
    const [isBind, setIsBind] = useState<boolean>(false);

    const [showMenu, setShowMenu] = useState<boolean>(false);

    const [showFpvModal, setShowFpvModel] = useState<boolean>(false);

    const modemCardRefs = useRef<ImeiMap>({});

    // Поиск, чипсы и группировка. Карта получает отсюда же набор видимых IMEI,
    // чтобы показывать ровно тех дронов, что остались в списке.
    const droneFilter = useDroneFilter(drones);

    // Позиции и телеметрия всего парка: карта и сетка берут их отсюда.
    const fleet = useFleetState(droneHub.connection);
    const attention = useDroneTriage(drones, fleet.telemetry);

    // Режим просмотра запоминается: оператор, выбравший сетку, не должен
    // переключать её при каждом заходе.
    const [view, setView] = useState<ConsoleView>(() => {
        try {
            return localStorage.getItem(VIEW_STORAGE_KEY) === 'grid' ? 'grid' : 'map';
        } catch {
            // Приватный режим или заблокированные данные сайта.
            return 'map';
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem(VIEW_STORAGE_KEY, view);
        } catch {
            // Не сохранилось — не повод ломать экран.
        }
    }, [view]);

    // Имена для подписей маркеров: позиции приходят с сервера отдельным
    // запросом и имени в себе не несут.
    const droneNames = useMemo<Record<string, string>>(() => {
        const names: Record<string, string> = {};
        drones.forEach((d) => { names[d.imei] = d.name; });
        return names;
    }, [drones]);

    const handleCloseMenu = useCallback((): void => {
        setShowMenu(false);
    }, []);

    const toggleShowMenu = useCallback((): void => {
        setShowMenu((s) => !s);
    }, []);

    // const consoleNavMsgClick = useCallback((): void => {
    //     setShowMsgs((s) => !s);
    // }, []);

    useEffect(() => {
        logger.info("console mount");
        getDronesInfo();
    }, []);

    useEffect(() => {
        if (updateOnlineDroneFromHub != null) {
            const ddd = drones.map(d => {
                const temp = Object.assign({}, d);
                if (updateOnlineDroneFromHub.imei === temp.imei) {
                    temp.isOnline = updateOnlineDroneFromHub.isOnline
                }
                return temp;
            });
            setDrones(ddd);
        }
    }, [updateOnlineDroneFromHub]);

    useEffect(() => {
        if (updateHbFromHub != null) {
            let update = false;
            const ddd = drones.map(d => {
                const temp = Object.assign({}, d);
                if (updateHbFromHub.imei === temp.imei) {
                    if (updateHbFromHub.isArm !== temp.isArm) {
                        //todo: refresh only arm
                        update = true;
                    }
                    temp.isArm = updateHbFromHub.isArm;
                    temp.mode = updateHbFromHub.mode;
                }
                /*if(drone?.imei === temp.imei) {
                    setDrone(temp);
                    logger.info('Set drone tmp:')
                    logger.info(temp);
                }*/
                return temp;
            });
            //logger.info(ddd);
            if (update) {
                setDrones(ddd);
            }
        }
    }, [updateHbFromHub]);

    useEffect(() => {
        const { connection } = droneHub;
        connection.on('SetOnline', setUpdateDroneFromHub);
        connection.on('HbStatus', setUpdateHbFromHub);

        return () => {
            connection.off('SetOnline', setUpdateDroneFromHub);
            connection.off('HbStatus', setUpdateHbFromHub);
        };
    }, [droneHub]);

    function getDronesInfo(selectImei?: string): void {
        APIRequestGet(`/drone/getModems`, {
            schema: MultipleModemsSchema,
            logSuccess: true
        }).then((result) => {
            setDrones(result.data.modemsInfo);
            setIsConfirmEmail(result.data.isConfirmEmail);
            setIsNebosvodAuth(result.data.isNebosvodAuth);
            setNebosvodLogin(result.data.nebosvodLogin);
            setStartMsgs(result.data.startMsgs);
            setIsLoad(true);
            if (selectImei) {
                const bound = result.data.modemsInfo.find(d => d.imei === selectImei);
                if (bound) {
                    setIsBind(false);
                    setDrone(bound);
                }
            }
        }).catch((error) => {
            // 401 разбирает интерцептор axiosInstance, он же и редиректит.
            logger.info(error);
        });
    }

    const nebosvodLogin = useCallback((nebosvodLogin: string): void => {
        setIsNebosvodAuth(true)
        setNebosvodLogin(nebosvodLogin)
        logger.info("nebosvod login: " + nebosvodLogin)
    }, []);

    // Модем привязан: перечитываем список и выбираем новое устройство,
    // без window.location.reload().
    const bindIsOk = useCallback((imei: string): void => {
        logger.info("bind is ok, refresh modems: " + imei);
        getDronesInfo(imei);
    }, []);

    const modemCardClick = useCallback((imei: string): void => {
        const selected = drones.find(d => d.imei === imei);
        if (selected) {
            setIsBind(false);
            setDrone(selected);
            setMapFocusOnDrone(true);
        }
    }, [drones]);

    const markerClick = useCallback((imei: string): void => {
        const selected = drones.find(d => d.imei === imei);
        if (selected) {
            setIsBind(false);
            setDrone(selected);
            scrollToModemCard(imei);
        }
    }, [drones]);

    const registerCard = useCallback((imei: string, el: HTMLDivElement | null): void => {
        if (el) {
            modemCardRefs.current[imei] = el;
        } else {
            delete modemCardRefs.current[imei];
        }
    }, []);

    const scrollToModemCard = (imei: string): void => {
        const el = modemCardRefs.current[imei];
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    // const callback = (id, phase, actualTime, baseTime, startTime, commitTime) => {
    //     logger.info(`${id}'s ${phase} phase: Actual: ${actualTime} ms`);
    // }

    const unselectModem = (): void => {
        setDrone(null);
        setMode("normal");
    }

    function isModemSettingsReady(drone: ModemData | null): drone is ModemData {
        return isLoad && !(isBind || (drone == null || drone.imei == null));
    }

    const isGrid = view === 'grid' && mode !== 'fpv';

    const gridSelect = useCallback((imei: string): void => {
        setView('map');
        modemCardClick(imei);
    }, [modemCardClick]);

    const onCloseFpvMode = (): void => {
        setMode("normal");
    }

    return (
        <div className={'console-page' + (drone ? " drone-selected" : "")}>
            {drone == null ? (
                <ConsoleNavMenu counts={droneFilter.counts} view={view} onViewChange={setView} />
            ) : (
                <ConsoleNavMenu
                    drone={drone}
                    unselectModem={unselectModem}
                    toggleShowMenu={toggleShowMenu}
                    startMsgs={startMsgs}
                />
            )}
            {/* Баннер отдельно от точки и вне обеих веток шапки: он должен
                держаться и в FPV, и на мобильном с выбранным дроном, когда
                панель списка вместе с точкой скрыта. */}
            <ConnectionStatus connection={droneHub.connection} variant='banner' />
            {access.ConsoleNotice && <access.ConsoleNotice />}
            {mode === "fpv" && drone &&
                <Fpv drone={drone} onClose={onCloseFpvMode} />
            }
            <div className={'console-container' + (isGrid ? ' console-container-grid' : '')}>
                {isGrid ? (
                    <DroneGrid
                        drones={droneFilter.groups.flatMap((g) => g.drones)}
                        positions={fleet.positions}
                        telemetry={fleet.telemetry}
                        attention={attention}
                        selectedImei={drone?.imei ?? null}
                        onSelect={gridSelect}
                    />
                ) : (
                <>
                <ConsoleMap
                    currentDrone={drone}
                    updateFocus={mapFocusOnDrone}
                    markerClick={markerClick}
                    visibleImeis={droneFilter.visibleImeis}
                    droneNames={droneNames}
                    positions={fleet.positions}
                />
                <div className='console-layout'>
                    {mode !== "fpv" &&
                        <div className={'console-modem-cards' + (isModemSettingsReady(drone) ? ' modem-selected' : '')}>
                            <DroneList
                                filter={droneFilter}
                                selectedImei={drone?.imei ?? null}
                                onSelect={modemCardClick}
                                registerCard={registerCard}
                                status={<ConnectionStatus connection={droneHub.connection} />}
                                footer={access.AddDroneCard &&
                                    <access.AddDroneCard
                                        onClick={() => {
                                            setIsBind(true);
                                            setDrone(null);
                                        }}
                                    />
                                }
                            />
                        </div>
                    }

                    {mode !== "fpv" && isModemSettingsReady(drone) &&
                        <>
                            <WebRtc drone={drone} mode={mode} />
                            <div className="console-layout-right">
                                <NavigationData drone={drone} />
                                <FpvModeButton onClick={() => { setShowFpvModel(true) }} />
                                <Modal show={showFpvModal} onHide={() => { setShowFpvModel(false) }}>
                                    <Modal.Header closeButton>
                                        <Modal.Title style={{ color: "#000000" }}>Осторожно</Modal.Title>
                                    </Modal.Header>
                                    <Modal.Body style={{ color: "#000000" }}>
                                        При переходе в FPV режим, вы перехватите управление, если уже кто-то подключен через Astra
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button variant="secondary" onClick={() => { setShowFpvModel(false) }}>
                                            Закрыть
                                        </Button>
                                        <Button variant="primary" onClick={() => { setMode('fpv'); setShowFpvModel(false); }}>
                                            Продолжить
                                        </Button>
                                    </Modal.Footer>
                                </Modal>
                            </div>
                        </>
                    }

                    {isLoad && (isBind || (drone == null || drone.imei == null)) &&
                        <access.ConsoleEmptyState droneCount={drones.length} onBindOk={bindIsOk} />
                    }
                </div>
                </>
                )}
            </div>
            {isLoad && !(isBind || (drone == null || drone.imei == null)) &&
                <Offcanvas show={showMenu} onHide={handleCloseMenu} name="test name" placement='end'>
                    <Offcanvas.Body>
                        <div className="offcanvas-close-btn" onClick={handleCloseMenu}></div>
                        <ModemSettings drone={drone} isNebosvodAuth={isNebosvodAuth} nebosvodAuthLogin={nebosvodAuthLogin} onNebosvodAuth={nebosvodLogin} />
                    </Offcanvas.Body>
                </Offcanvas>
            }
        </div>
    )
}

export default Console;